import React, { useEffect, useRef, useState } from 'react';
import {
  setAgentAvailable,
  setAgentBreak,
  setAgentBusy,
  setAgentOffline,
  subscribeCurrentAgentStatus,
} from '../lib/agentPresence';

interface AgentStatusWidgetProps {
  agentId: string;
  onStatusChange?: (status: string) => void;
  active?: boolean;
  variant?: 'buttons' | 'menu';
}

type AgentStatus = 'available' | 'busy' | 'break' | 'offline';

export const AgentStatusWidget: React.FC<AgentStatusWidgetProps> = ({
  agentId,
  onStatusChange,
  active = true,
  variant = 'buttons',
}) => {
  const [status, setStatus] = useState<AgentStatus>('offline');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (!active) return;
    return subscribeCurrentAgentStatus(agentId, (record) => {
      const statusValue = record?.status;
      setStatus(
        statusValue === 'available' ||
          statusValue === 'busy' ||
          statusValue === 'break' ||
          statusValue === 'offline'
          ? statusValue
          : 'offline'
      );
      setError(null);
    });
  }, [active, agentId]);

  const updateStatus = async (newStatus: AgentStatus) => {
    setLoading(true);
    try {
      const user = { email: agentId };
      if (newStatus === 'available') {
        await setAgentAvailable(user);
      } else if (newStatus === 'busy') {
        await setAgentBusy(user, null);
      } else if (newStatus === 'break') {
        await setAgentBreak(user);
      } else {
        await setAgentOffline(user);
      }
      setStatus(newStatus);
      setError(null);
      onStatusChange?.(newStatus);
      menuRef.current?.removeAttribute('open');
    } catch (error) {
      console.error('Failed to update agent status:', error);
      setError('Could not update agent status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (currentStatus: AgentStatus) => {
    switch (currentStatus) {
      case 'available':
        return 'bg-green-500';
      case 'busy':
        return 'bg-yellow-500';
      case 'break':
        return 'bg-orange-500';
      case 'offline':
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (currentStatus: AgentStatus) => {
    switch (currentStatus) {
      case 'available':
        return 'Available';
      case 'busy':
        return 'Busy';
      case 'break':
        return 'Break';
      case 'offline':
      default:
        return 'Offline';
    }
  };

  if (variant === 'menu') {
    return (
      <div className="relative">
        <details ref={menuRef} className="group relative">
          <summary className="flex h-8 cursor-pointer list-none items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 [&::-webkit-details-marker]:hidden">
            <span className={`h-2 w-2 rounded-full ${getStatusColor(status)}`} />
            <span>{getStatusText(status)}</span>
            <span aria-hidden="true" className="text-[10px] text-slate-400 transition-transform group-open:rotate-180">⌄</span>
          </summary>
          <div className="absolute right-0 top-10 z-50 w-36 overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
            {(['available', 'busy', 'break', 'offline'] as AgentStatus[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => updateStatus(option)}
                disabled={loading}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium hover:bg-slate-50 disabled:opacity-50 ${
                  status === option ? 'text-emerald-700' : 'text-slate-700'
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${getStatusColor(option)}`} />
                <span>{getStatusText(option)}</span>
              </button>
            ))}
          </div>
        </details>
        {error && <div className="absolute right-0 top-10 z-[60] whitespace-nowrap rounded bg-red-50 px-2 py-1 text-[10px] font-medium text-red-600 shadow">{error}</div>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="w-[76px] flex-none">
          <h3 className="text-xs font-semibold leading-tight text-slate-900">Agent Status</h3>
          <div className="mt-1 flex items-center gap-1.5">
            <div className={`h-2 w-2 rounded-full ${getStatusColor(status)}`} />
            <span className="truncate text-[10px] font-medium text-slate-600">{getStatusText(status)}</span>
          </div>
        </div>
        <div className="grid min-w-0 flex-1 grid-cols-4 gap-1">
          {(['available', 'busy', 'break', 'offline'] as AgentStatus[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => updateStatus(option)}
              disabled={loading}
              className={`h-8 min-w-0 rounded-lg px-1 text-[10px] font-semibold transition-colors ${
                status === option
                  ? 'bg-green-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              } disabled:opacity-50`}
            >
              {getStatusText(option)}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="mt-1.5 text-[10px] font-medium text-red-600">{error}</div>}
    </div>
  );
};
